/**
 * Google Analytics 4 Tracking Service
 * Tracks user behavior across storefronts, promotions, and discovery patterns
 */

const gaTrackingService = {
  /**
   * Check if GA4 is available
   */
  isAvailable() {
    return typeof window !== 'undefined' && typeof window.gtag !== 'undefined';
  },

  /**
   * Track storefront view
   * @param {Object} params - Store details
   */
  trackStorefrontView(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'storefront_view', {
      store_id: params?.storeId,
      store_name: params?.storeName,
      category: params?.category,
      zone: params?.zone,
      is_featured: params?.isFeatured || false,
      has_promotion: params?.hasPromotion || false,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track storefront card click
   * @param {Object} params - Store details
   */
  trackStorefrontClick(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'storefront_click', {
      store_id: params?.storeId,
      store_name: params?.storeName,
      category: params?.category,
      zone: params?.zone,
      click_source: params?.source || 'card', // card, carousel, list
      page_path: window.location?.pathname
    });
  },

  /**
   * Track storefront save/unsave
   * @param {Object} params - Store details and action
   */
  trackStorefrontSave(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', params?.action === 'save' ? 'storefront_save' : 'storefront_unsave', {
      store_id: params?.storeId,
      store_name: params?.storeName,
      category: params?.category,
      zone: params?.zone,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track promotion view
   * @param {Object} params - Promotion details
   */
  trackPromotionView(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'promotion_view', {
      promotion_id: params?.promotionId,
      promotion_title: params?.promotionTitle,
      store_id: params?.storeId,
      store_name: params?.storeName,
      discount_value: params?.discountValue,
      promotion_type: params?.promotionType, // discount, offer, cashback
      page_path: window.location?.pathname
    });
  },

  /**
   * Track promotion click/engagement
   * @param {Object} params - Promotion details
   */
  trackPromotionClick(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'promotion_click', {
      promotion_id: params?.promotionId,
      promotion_title: params?.promotionTitle,
      store_id: params?.storeId,
      store_name: params?.storeName,
      discount_value: params?.discountValue,
      engagement_type: params?.engagementType || 'click', // click, share, view_details
      page_path: window.location?.pathname
    });
  },

  /**
   * Track search query
   * @param {Object} params - Search details
   */
  trackSearch(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'search', {
      search_term: params?.searchTerm,
      results_count: params?.resultsCount || 0,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track search suggestion click
   * @param {Object} params - Suggestion details
   */
  trackSearchSuggestionClick(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'search_suggestion_click', {
      suggestion_name: params?.suggestionName,
      suggestion_category: params?.suggestionCategory,
      search_term: params?.searchTerm,
      position: params?.position || 0,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track filter application
   * @param {Object} params - Filter details
   */
  trackFilterApply(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'filter_apply', {
      zone: params?.zone || 'all',
      category: params?.category || 'all',
      distance: params?.distance,
      sort_by: params?.sortBy,
      has_promotion: params?.hasPromo || false,
      has_cashback: params?.hasCashback || false,
      open_now: params?.openNow || false,
      featured_only: params?.featured || false,
      active_filter_count: params?.activeFilterCount || 0,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track filter clear
   */
  trackFilterClear() {
    if (!this.isAvailable()) return;

    window.gtag('event', 'filter_clear', {
      page_path: window.location?.pathname
    });
  },

  /**
   * Track zone selection
   * @param {Object} params - Zone details
   */
  trackZoneSelection(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'zone_selection', {
      zone: params?.zone,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track category selection
   * @param {Object} params - Category details
   */
  trackCategorySelection(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'category_selection', {
      category: params?.category,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track store detail tab navigation
   * @param {Object} params - Tab details
   */
  trackTabNavigation(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'tab_navigation', {
      store_id: params?.storeId,
      store_name: params?.storeName,
      tab_name: params?.tabName, // vetrina, promo, prodotti, servizi, recensioni
      page_path: window.location?.pathname
    });
  },

  /**
   * Track image gallery navigation
   * @param {Object} params - Gallery details
   */
  trackImageNavigation(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'image_navigation', {
      store_id: params?.storeId,
      direction: params?.direction, // next, prev
      image_index: params?.imageIndex,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track map navigation
   */
  trackMapNavigation() {
    if (!this.isAvailable()) return;

    window.gtag('event', 'map_navigation', {
      source_page: window.location?.pathname
    });
  },

  /**
   * Track featured carousel interaction
   * @param {Object} params - Carousel details
   */
  trackCarouselInteraction(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'carousel_interaction', {
      store_id: params?.storeId,
      store_name: params?.storeName,
      interaction_type: params?.interactionType, // click, swipe, auto_scroll
      position: params?.position || 0,
      page_path: window.location?.pathname
    });
  },

  /**
   * Track user engagement time on storefront
   * @param {Object} params - Engagement details
   */
  trackEngagementTime(params) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'user_engagement', {
      store_id: params?.storeId,
      engagement_time_msec: params?.timeSpent,
      page_path: window.location?.pathname
    });
  }
};

export default gaTrackingService;